import { isOrganizationOwner } from './organizationOwner';

/**
 * Uprawnienia do operacji finansowych punktu (#199): lustro `paymentsAccess.ts`
 * z functions-payments. Źródłem prawdy zostaje backend (403), panel tylko nie
 * proponuje akcji, która skończy się odmową.
 *
 * - Konfiguracja Stripe (onboarding, wpięcie i odpięcie punktu, odłączenie konta,
 *   włączenie płatności, akceptacja i odrzucenie regulaminu): właściciel TEGO hotelu
 *   albo administrator HotelSpot (`canManageOrganization`).
 * - Zwrot: jak wyżej ALBO konto z rolą `manager` / uprawnieniem panelu `all`.
 *
 * `staffOnly` celowo pomijamy: backend odrzuca takie konto także tutaj (`canManageOrganization`,
 * `isRefundManagerClaims`), ale odpada ono wcześniej, na `authorizeHotelAccess`, a w panelu
 * i tak zatrzymuje je `StaffOnlyGate`.
 */

/** Kod 403 z `v3-refundOutletPayment`. */
export const REFUND_FORBIDDEN = 'payments.refundForbidden';
/** Kod 403 z endpointów konfiguracji Stripe. */
export const STRIPE_CONFIG_FORBIDDEN = 'payments.stripeConfigForbidden';

export interface PaymentsAccessInput {
  uid: string | null | undefined;
  organizationId: string | null | undefined;
  /** `organizations/{id}.owner` bieżącej organizacji. */
  organizationOwner: string | null | undefined;
  /** Claim `owner`: tablica id organizacji albo jedno id (starsze konta). */
  ownerClaim: string[] | string | null | undefined;
  /** Admin HotelSpot: claim `isAdmin` / `isHotelspotAdmin` albo flaga w `users/{uid}`. */
  isAdmin: boolean;
  /** Claim `role` (tablica, na starszych kontach pojedynczy string). */
  role?: string[] | string | null;
  /** Claim `permissions`. */
  permissions?: string[] | string | null;
}

const listHas = (value: string[] | string | null | undefined, item: string): boolean => (
  Array.isArray(value) ? value.includes(item) : value === item
);

/** Właściciel tego hotelu albo administrator HotelSpot (backend: `canManageOrganization`). */
export const canConfigureStripe = (input: PaymentsAccessInput): boolean => input.isAdmin
  || isOrganizationOwner({
    uid: input.uid,
    organizationId: input.organizationId,
    organizationOwner: input.organizationOwner,
    ownerClaim: input.ownerClaim,
  });

/** Zwrot: jak konfiguracja Stripe albo rola `manager` / uprawnienie `all` (backend: `authorizeRefund`). */
export const canRequestRefund = (input: PaymentsAccessInput): boolean => {
  if (!input.uid) return false;
  if (listHas(input.role, 'manager') || listHas(input.permissions, 'all')) return true;
  return canConfigureStripe(input);
};

/** `response.data.message` z błędu axiosa (kod `payments.*`), bez zależności od axiosa. */
export const backendErrorCode = (error: unknown): string => {
  const message = (error as { response?: { data?: { message?: unknown } } } | null)?.response?.data?.message;
  return typeof message === 'string' ? message : '';
};

/** `response.data.data` z błędu axiosa: szczegóły do zdania (np. minimum kwoty). */
const backendErrorData = (error: unknown): Record<string, unknown> => {
  const data = (error as { response?: { data?: { data?: unknown } } } | null)?.response?.data?.data;
  return data && typeof data === 'object' ? data as Record<string, unknown> : {};
};

type Translate = (key: string, fallback: string, options?: Record<string, unknown>) => unknown;

/** Klucz słownika i polski fallback dla kodów odmowy wspólnych dla wszystkich ekranów płatności. */
const FORBIDDEN_MESSAGES: Record<string, [string, string]> = {
  [REFUND_FORBIDDEN]: [
    'payments.errors.refundForbidden',
    'Zwrot może zlecić właściciel hotelu lub menedżer.',
  ],
  [STRIPE_CONFIG_FORBIDDEN]: [
    'payments.errors.stripeConfigForbidden',
    'Konfigurację płatności Stripe może zmienić tylko właściciel hotelu.',
  ],
};

/** Ogólny błąd functions-payments (#200): zamiast treści wyjątku backend odsyła ten klucz. */
export const PAYMENTS_UNEXPECTED_ERROR = 'payments.unexpectedError';
/** Kwota poniżej minimum Stripe'a; w `data` przychodzą `minimumAmount` i `currency`. */
export const PAYMENTS_AMOUNT_BELOW_MINIMUM = 'payments.amountBelowMinimum';

/**
 * Klucze, które functions-payments odsyła zamiast angielskiej treści błędu Stripe'a (#200).
 * Wspólne dla wszystkich ekranów: to samo zdanie niezależnie od tego, która operacja padła.
 */
const STRIPE_MESSAGES: Record<string, [string, string]> = {
  'payments.error.stripeUnavailable': [
    'payments.errors.stripeUnavailable',
    'Stripe chwilowo nie odpowiada. Spróbuj ponownie za kilka minut.',
  ],
  'payments.error.stripeRejected': [
    'payments.errors.stripeRejected',
    'Stripe odrzucił tę operację. Sprawdź stan konta i płatności w panelu Stripe.',
  ],
  'payments.error.stripeConfig': [
    'payments.errors.stripeConfig',
    'Połączenie ze Stripe jest źle skonfigurowane. Skontaktuj się z pomocą HotelSpot.',
  ],
  'payments.error.cardDeclined': [
    'payments.errors.cardDeclined',
    'Bank odrzucił płatność kartą.',
  ],
  'payments.intentCanceled': [
    'payments.errors.intentCanceled',
    'Ta płatność została anulowana. Rozpocznij ją od nowa.',
  ],
};

/** „2,00 zł" w języku przeglądarki; przy nieznanej walucie liczba z kodem. */
const formatMinimum = (amount: unknown, currency: unknown): string => {
  if (typeof amount !== 'number') return '';
  if (typeof currency !== 'string' || !currency) return String(amount);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
};

/**
 * Jeden przelicznik „błąd z backendu → zdanie dla hotelarza" dla ekranów płatności.
 * Kolejność: odmowa uprawnień (wspólna), słownik ekranu, wspólne błędy Stripe'a (#200),
 * na końcu komunikat ekranu.
 *
 * `payments.unexpectedError` celowo daje komunikat ekranu („Nie udało się zlecić zwrotu"):
 * mówi więcej niż ogólne „wystąpił błąd". Ogólne zdanie zostaje, gdy ekran nie podał własnego.
 */
export const paymentsErrorMessage = (
  error: unknown,
  t: Translate,
  fallback: string,
  screenMessages: Record<string, string> = {},
): string => {
  const code = backendErrorCode(error);
  const forbidden = FORBIDDEN_MESSAGES[code];
  if (forbidden) return String(t(forbidden[0], forbidden[1]));
  if (screenMessages[code]) return screenMessages[code];
  const stripe = STRIPE_MESSAGES[code];
  if (stripe) return String(t(stripe[0], stripe[1]));
  if (code === PAYMENTS_AMOUNT_BELOW_MINIMUM) {
    const data = backendErrorData(error);
    const amount = formatMinimum(data.minimumAmount, data.currency);
    return amount
      ? String(t(
        'payments.errors.amountBelowMinimum',
        'Kwota jest niższa niż minimum płatności online ({{amount}}).',
        { amount },
      ))
      : String(t('payments.errors.amountBelowMinimumGeneric', 'Kwota jest niższa niż minimum płatności online.'));
  }
  if (code === PAYMENTS_UNEXPECTED_ERROR && !fallback) {
    return String(t('payments.errors.unexpected', 'Wystąpił nieoczekiwany błąd płatności. Spróbuj ponownie.'));
  }
  return fallback;
};

/** Tekst podpowiedzi przy zablokowanych kontrolkach (ten sam co przy odmowie z backendu). */
export const refundForbiddenText = (t: Translate): string => String(t(...FORBIDDEN_MESSAGES[REFUND_FORBIDDEN]));
export const stripeConfigForbiddenText = (t: Translate): string => String(
  t(...FORBIDDEN_MESSAGES[STRIPE_CONFIG_FORBIDDEN]),
);
